import { useFilterStore } from '@/features/filters/store/filter-store';
import { useLocationStore } from '@/features/location/store/location-store';
import { useGoogleMapsStore } from '@/features/map/store/google-maps-store';

/**
 * Handlers that move the map to a searched place or the user's location.
 */
export function usePlaceNavigation() {
  const setMapCenter = useLocationStore((s) => s.setMapCenter);
  const setZoomLevel = useLocationStore((s) => s.setZoomLevel);
  const getCurrentLocation = useLocationStore((s) => s.getCurrentLocation);
  const isFetchingLocation = useLocationStore((s) => s.isFetchingLocation);
  const mapInstance = useGoogleMapsStore((s) => s.mapInstance);
  const setAnchorPointVisible = useFilterStore((s) => s.setAnchorPointVisible);

  const handlePlaceSelect = async (placeId: string) => {
    // Use the new Place API instead of the deprecated PlacesService
    if (!google?.maps?.places?.Place) {
      console.error('[usePlaceNavigation] Google Maps Places API not loaded');
      return;
    }

    try {
      const place = new google.maps.places.Place({ id: placeId });
      await place.fetchFields({ fields: ['location'] });

      const placeLocation = place.location;
      if (!placeLocation) {
        console.error('[usePlaceNavigation] Place location not found');
        return;
      }

      const location = { lat: placeLocation.lat(), lng: placeLocation.lng() };
      setMapCenter(location);
      setZoomLevel(15);

      if (mapInstance) {
        mapInstance.panTo(location);
        mapInstance.setZoom(15);
      }

      // Deliberate search: surface the anchor pin where the user landed
      setAnchorPointVisible(true);
    } catch (error) {
      console.error(
        '[usePlaceNavigation] Error fetching place details:',
        error
      );
    }
  };

  const handleCurrentLocation = async () => {
    await getCurrentLocation();

    // Read the updated position after getCurrentLocation completes
    const { currentPosition } = useLocationStore.getState();
    if (currentPosition && mapInstance) {
      mapInstance.panTo(currentPosition);
      mapInstance.setZoom(15);
    }

    // Deliberate search: surface the anchor pin where the user landed
    setAnchorPointVisible(true);
  };

  return { handlePlaceSelect, handleCurrentLocation, isFetchingLocation };
}
